import React, { useCallback, useEffect, useMemo, useState } from 'react'
import apiClient, { API_ENDPOINTS, getUserInfoSync } from '../../config/api'

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2
})

const formatDateTime = (value) => {
  if (!value) {
    return 'N/A'
  }

  try {
    return new Date(value).toLocaleString()
  } catch (error) {
    return value
  }
}

const STATUS_FILTERS = [
  { label: 'All statuses', value: 'all' },
  { label: 'Pending', value: 'pending' },
  { label: 'Completed', value: 'completed' },
  { label: 'Cancelled', value: 'cancelled' }
]

const MyRequests = () => {
  const userInfo = getUserInfoSync()
  const currentUserEmail = userInfo?.email?.toLowerCase() || ''
  const currentUserId = userInfo?.id || userInfo?.user_id || null

  const [requests, setRequests] = useState([])
  const [loading, setLoading] = useState(true)
  const [errorMessage, setErrorMessage] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [expandedRows, setExpandedRows] = useState(new Set())

  const matchesCurrentUser = useCallback(
    (transfer) => {
      const creatorEmail = transfer.created_by_email?.toLowerCase()
      if (currentUserEmail && creatorEmail) {
        return creatorEmail === currentUserEmail
      }

      if (currentUserId && transfer.created_by) {
        return String(transfer.created_by) === String(currentUserId)
      }

      return false
    },
    [currentUserEmail, currentUserId]
  )

  const fetchRequests = useCallback(async () => {
    try {
      setLoading(true)
      setErrorMessage('')

      const response = await apiClient.get(API_ENDPOINTS.STOCK_TRANSFERS)
      const data = Array.isArray(response.data) ? response.data : response.data?.results || []

      const userRequests = data.filter(matchesCurrentUser)
      setRequests(userRequests)
    } catch (error) {
      console.error('Error fetching my requests:', error)
      setRequests([])
      setErrorMessage(
        error.response?.data?.detail ||
          error.response?.data?.message ||
          'Failed to load your stock requests. Please try again.'
      )
    } finally {
      setLoading(false)
    }
  }, [matchesCurrentUser])

  useEffect(() => {
    fetchRequests()
  }, [fetchRequests])

  const handleToggleRow = (requestId) => {
    const updated = new Set(expandedRows)
    if (updated.has(requestId)) {
      updated.delete(requestId)
    } else {
      updated.add(requestId)
    }
    setExpandedRows(updated)
  }

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-success'
      case 'pending':
        return 'bg-warning'
      case 'cancelled':
        return 'bg-danger'
      default:
        return 'bg-secondary'
    }
  }

  const filteredRequests = useMemo(() => {
    return requests.filter((request) => {
      const matchesStatus = statusFilter === 'all' || request.status === statusFilter

      if (!matchesStatus) {
        return false
      }

      if (!searchTerm) {
        return true
      }

      const term = searchTerm.toLowerCase()
      return (
        request.reference_number?.toLowerCase().includes(term) ||
        request.transfer_type_display?.toLowerCase().includes(term) ||
        request.destination_branch_name?.toLowerCase().includes(term) ||
        request.destination_warehouse_name?.toLowerCase().includes(term) ||
        request.notes?.toLowerCase().includes(term)
      )
    })
  }, [requests, searchTerm, statusFilter])

  return (
    <>
      <div className="pagetitle">
        <h1>My Stock Requests</h1>
        <nav>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">Home</li>
            <li className="breadcrumb-item">Stock Transfers</li>
            <li className="breadcrumb-item active">My Requests</li>
          </ol>
        </nav>
      </div>

      <section className="section">
        <div className="row">
          <div className="col-lg-12">
            <div className="card">
              <div className="card-body">
                <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 mb-4">
                  <h5 className="card-title mb-0">Requests created by you</h5>
                  <div className="d-flex flex-column flex-sm-row gap-2">
                    <select
                      className="form-select"
                      style={{ minWidth: '180px' }}
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      disabled={loading}
                    >
                      {STATUS_FILTERS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    <button className="btn btn-primary" onClick={fetchRequests} disabled={loading}>
                      <i className="bi bi-arrow-clockwise me-2"></i>
                      Refresh
                    </button>
                  </div>
                </div>

                <div className="mb-3">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search by reference, type, destination, or notes..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    disabled={loading && !requests.length}
                  />
                </div>

                {errorMessage && (
                  <div className="alert alert-danger alert-dismissible fade show" role="alert">
                    {errorMessage}
                    <button type="button" className="btn-close" onClick={() => setErrorMessage('')}></button>
                  </div>
                )}

                {loading ? (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-3 text-muted">Loading your requests...</p>
                  </div>
                ) : filteredRequests.length === 0 ? (
                  <div className="text-center py-5">
                    <i className="bi bi-inbox fs-1 text-muted"></i>
                    <p className="mt-3 text-muted">
                      {searchTerm
                        ? 'No requests found matching your search.'
                        : 'You have not created any stock requests yet.'}
                    </p>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-hover">
                      <thead className="table-light">
                        <tr>
                          <th style={{ width: '40px' }}></th>
                          <th>Reference</th>
                          <th>Type</th>
                          <th>Destination</th>
                          <th>Items</th>
                          <th>Total Cost</th>
                          <th>Status</th>
                          <th>Created</th>
                          <th>Last Updated</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredRequests.map((request) => (
                          <React.Fragment key={request.id}>
                            <tr style={{ cursor: 'pointer' }} onClick={() => handleToggleRow(request.id)}>
                              <td>
                                <i className={`bi bi-chevron-${expandedRows.has(request.id) ? 'down' : 'right'}`}></i>
                              </td>
                              <td>
                                <strong>{request.reference_number || 'Pending assignment'}</strong>
                              </td>
                              <td>{request.transfer_type_display || 'N/A'}</td>
                              <td>
                                {request.destination_branch_name ||
                                  request.destination_warehouse_name || (
                                    <span className="text-muted">N/A</span>
                                  )}
                              </td>
                              <td>
                                <span className="badge bg-info">
                                  {request.item_count || request.items?.length || 0}
                                </span>
                              </td>
                              <td>
                                <strong>{currencyFormatter.format(request.total_cost || 0)}</strong>
                              </td>
                              <td>
                                <span className={`badge ${getStatusBadgeClass(request.status)}`}>
                                  {request.status_display || request.status}
                                </span>
                              </td>
                              <td>
                                <small>{formatDateTime(request.created_at)}</small>
                              </td>
                              <td>
                                <small>{request.updated_at ? formatDateTime(request.updated_at) : 'N/A'}</small>
                              </td>
                            </tr>
                            {expandedRows.has(request.id) && (
                              <tr>
                                <td colSpan="9" className="bg-light">
                                  <div className="p-4">
                                    <div className="row">
                                      <div className="col-md-6 mb-3">
                                        <small className="text-muted d-block">Source</small>
                                        <p className="mb-0">
                                          {request.source_branch ||
                                            request.source_warehouse_name || (
                                              <em className="text-muted">N/A</em>
                                            )}
                                        </p>
                                      </div>
                                      <div className="col-md-6 mb-3">
                                        <small className="text-muted d-block">Notes</small>
                                        <p className="mb-0">{request.notes || <em className="text-muted">None</em>}</p>
                                      </div>
                                    </div>

                                    <h6 className="mb-3 mt-4">Transfer Items</h6>
                                    {request.items && request.items.length > 0 ? (
                                      <div className="table-responsive">
                                        <table className="table table-sm table-bordered">
                                          <thead className="table-secondary">
                                            <tr>
                                              <th>Product</th>
                                              <th>SKU</th>
                                              <th>Quantity</th>
                                              <th>Selling Price</th>
                                              <th>Total Cost</th>
                                              <th>Supplier</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {request.items.map((item) => (
                                              <tr key={item.id || item.product_id}>
                                                <td>{item.product_name || item.product}</td>
                                                <td>
                                                  {item.product_sku ? <code>{item.product_sku}</code> : 'N/A'}
                                                </td>
                                                <td>{item.quantity || 0}</td>
                                                <td>
                                                  {item.selling_price
                                                    ? currencyFormatter.format(Number(item.selling_price) || 0)
                                                    : 'N/A'}
                                                </td>
                                                <td>
                                                  <strong>
                                                    {currencyFormatter.format(Number(item.total_cost) || 0)}
                                                  </strong>
                                                </td>
                                                <td>{item.supplier_name || <span className="text-muted">N/A</span>}</td>
                                              </tr>
                                            ))}
                                          </tbody>
                                        </table>
                                      </div>
                                    ) : (
                                      <p className="text-muted mb-0">No line items available yet.</p>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}

export default MyRequests


